import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Text,
  Flex,
  Input,
  Button,
  useToast,
  Spinner,
  IconButton,
  Textarea,
  Tag,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { CloseIcon, DeleteIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { fetchCategories, createCategory, updateCategory } from '../../api/endpointsBackend';
import { ColorPicker, FALLBACK_CATEGORY_COLOR } from './CategoriesPage';
import CategoryTimelineChart, { SHORT_MONTHS } from './CategoryTimelineChart';
import CategoryKeyMetrics from './CategoryKeyMetrics';
import EmojiPickerField from '../shared/EmojiPickerField';
import Figure from '../shared/Figure';
import MovementsList from '../shared/MovementsList';
import MovementDetailPanel from '../movements/MovementDetailPanel';
import ConvertedTotal from '../shared/ConvertedTotal';
import { useConvertedTotal } from '../../hooks/useConvertedTotal';

// Money in reads green, money out in ink.
const TYPE_OPTIONS = [
  { value: 'Income', labelKey: 'income', color: 'redesign.amount.income' },
  { value: 'Expenses', labelKey: 'expenses', color: 'redesign.amount.expense' }
];

export default function CategoryDetailPanel({
  categoryId,
  category: categoryFromParent,
  categories,
  allTags,
  onClose,
  onCategoryUpdated,
  onDelete,
  monthlyTimeline,
  yearlyMetrics,
  dateRange,
  typeFilter,
  selectedMonthIdx,
  currentMonthIdx,
  onSelectMonth,
  effectiveCurrency,
}) {
  const { t } = useTranslation('categories');
  const isCreateMode = !categoryId;
  const movementsFilters = useMemo(
    () => categoryId ? [{ entity: 'Movement', column: 'category_id', operator: 'in', values: [categoryId] }] : null,
    [categoryId]
  );
  const shouldKeepAfterCategoryChange = useCallback(
    (m) => m.category_id === categoryId,
    [categoryId]
  );
  const [category, setCategory] = useState(null);
  const [editedCategory, setEditedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedMovementId, setSelectedMovementId] = useState(null);
  const [overlayDirty, setOverlayDirty] = useState(false);
  const [transactionsRefreshKey, setTransactionsRefreshKey] = useState(0);
  const toast = useToast();

  const handleOverlayMovementUpdated = useCallback(() => {
    setOverlayDirty(true);
  }, []);

  const handleCloseOverlay = useCallback(() => {
    setSelectedMovementId(null);
    if (overlayDirty) {
      setTransactionsRefreshKey(k => k + 1);
      setOverlayDirty(false);
      if (onCategoryUpdated) onCategoryUpdated();
    }
  }, [overlayDirty, onCategoryUpdated]);

  // Close the overlay when switching to a different category
  useEffect(() => { setSelectedMovementId(null); setOverlayDirty(false); }, [categoryId]);

  const selectedMonth = monthlyTimeline?.[selectedMonthIdx] || null;
  const selectedMonthRange = useMemo(() => {
    if (!selectedMonth) return dateRange;
    const { year, month } = selectedMonth;
    const pad = (n) => String(n).padStart(2, '0');
    const lastDay = new Date(year, month, 0).getDate();
    return {
      start_date: `${year}-${pad(month)}-01`,
      end_date: `${year}-${pad(month)}-${pad(lastDay)}`,
    };
  }, [selectedMonth, dateRange]);

  // MI-298: single approximate total converted to the chosen display currency,
  // scoped to this category and the selected month (matches the per-currency
  // "spent in month" figure). No-op until a display currency is picked.
  const { data: convertedTotal, loading: convertedLoading, displayCurrency } =
    useConvertedTotal(movementsFilters, {
      startDate: selectedMonthRange?.start_date,
      endDate: selectedMonthRange?.end_date,
    });

  const currentMonthName = dateRange?.start_date
    ? new Date(dateRange.start_date + 'T00:00:00').toLocaleString('en-US', { month: 'long' })
    : new Date().toLocaleString('en-US', { month: 'long' });

  const selectedMonthLabel = selectedMonth
    ? `${SHORT_MONTHS[selectedMonth.month - 1]} ${selectedMonth.year}`
    : currentMonthName;
  const selectedMonthTotals = useMemo(() => {
    if (!selectedMonth) return [];
    const byCurrency = {};
    selectedMonth.data
      .filter(d => d.category_id === categoryId)
      .forEach(d => {
        const c = d.currency || 'COP';
        byCurrency[c] = (byCurrency[c] || 0) + Math.abs(d.total || 0);
      });
    return Object.entries(byCurrency)
      .map(([currency, total]) => ({ currency, total }))
      .sort((a, b) => b.total - a.total);
  }, [selectedMonth, categoryId]);

  const fetchCategoryData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const categories = await fetchCategories();
      const foundCategory = categories.find(cat => cat.id === categoryId);
      if (foundCategory) {
        setCategory(foundCategory);
        setEditedCategory(foundCategory);
      } else {
        setError(t('detail.notFound'));
      }
    } catch (err) {
      console.error('Error fetching category:', err);
      setError(t('detail.loadError'));
      toast({ title: t('detail.toasts.errorTitle'), description: t('detail.loadError'), status: 'error', duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [categoryId, toast, t]);

  useEffect(() => {
    setShowEditForm(false);
    if (categoryId) {
      fetchCategoryData();
    } else {
      const empty = { name: '', type: 'Expenses', description: '', icon: '', color: '' };
      setCategory(empty);
      setEditedCategory(empty);
      setIsEditingName(true);
      setShowEditForm(true);
    }
  }, [categoryId, fetchCategoryData]);

  const handleSave = async () => {
    setUpdating(true);
    try {
      const payload = {
        name: editedCategory.name,
        type: editedCategory.type,
        description: editedCategory.description || '',
        icon: editedCategory.icon || '',
        color: editedCategory.color || ''
      };
      if (isCreateMode) {
        await createCategory(payload);
      } else {
        await updateCategory(categoryId, payload);
      }
      toast({
        title: t('detail.toasts.successTitle'),
        description: isCreateMode ? t('detail.toasts.createSuccess') : t('detail.toasts.updateSuccess'),
        status: 'success', duration: 3000, isClosable: true,
      });
      if (onCategoryUpdated) onCategoryUpdated();
      if (isCreateMode && onClose) onClose();
      setShowEditForm(false);
    } catch (err) {
      console.error(`Error ${isCreateMode ? 'creating' : 'updating'} category:`, err);
      toast({
        title: t('detail.toasts.errorTitle'),
        description: err.response?.data?.detail || (isCreateMode ? t('toasts.deleteFailed').replace('delete', 'create') : t('toasts.deleteFailed').replace('delete', 'update')),
        status: 'error', duration: 5000, isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteClick = () => {
    if (onDelete && category) {
      onClose();
      onDelete(category);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedCategory(prev => ({ ...prev, [field]: value }));
  };

  const catColor = category?.color || categoryFromParent?.color || FALLBACK_CATEGORY_COLOR;
  const catIcon = category?.icon || categoryFromParent?.icon || '📊';
  const catName = category?.name || categoryFromParent?.name || '';
  const spentCurrency = categoryFromParent?.currency || '';

  const renderEditForm = () => (
    <Box px={{ base: 2, md: 4 }} py={4}>
      {/* Name */}
      <Box mb={4}>
        {isEditingName ? (
          <Input
            value={editedCategory?.name || ''}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            onBlur={() => setIsEditingName(false)}
            autoFocus
            fontSize="xl"
            fontWeight="600"
            variant="unstyled"
            placeholder={t('detail.fields.name')}
          />
        ) : (
          <Text
            fontSize="xl"
            fontWeight="600"
            onClick={() => setIsEditingName(true)}
            cursor="pointer"
            _hover={{ color: "redesign.accent.blue" }}
          >
            {editedCategory?.name || t('detail.fields.addName')}
          </Text>
        )}
        <Popover placement="bottom-start">
          {({ onClose: closeTypePopover }) => (
            <>
              <PopoverTrigger>
                <Flex align="center" gap={1} cursor="pointer" _hover={{ opacity: 0.8 }} mt={1}>
                  <Tag
                    size="sm"
                    colorScheme={editedCategory?.type === 'Income' ? 'green' : 'gray'}
                    variant="subtle"
                    borderRadius="full"
                    px={3} py={1}
                  >
                    {editedCategory?.type === 'Income' ? t('detail.types.income') : t('detail.types.expenses')}
                  </Tag>
                </Flex>
              </PopoverTrigger>
              <PopoverContent w="150px" borderRadius="lg">
                <PopoverBody p={1}>
                  {TYPE_OPTIONS.map(opt => (
                    <Flex
                      key={opt.value}
                      px={3} py={2}
                      cursor="pointer"
                      borderRadius="md"
                      bg={opt.value === editedCategory?.type ? "redesign.bg.accent" : "transparent"}
                      _hover={{ bg: "redesign.bg.hover" }}
                      onClick={() => { handleFieldChange('type', opt.value); closeTypePopover(); }}
                    >
                      <Text fontSize="sm" fontWeight={opt.value === editedCategory?.type ? "600" : "400"} color={opt.color}>
                        {t(`detail.types.${opt.labelKey}`)}
                      </Text>
                    </Flex>
                  ))}
                </PopoverBody>
              </PopoverContent>
            </>
          )}
        </Popover>
      </Box>

      {/* Fields */}
      <Flex py="8px" align="flex-start">
        <Text fontSize={{ base: "sm", md: "md" }} color="redesign.text.muted" w={{ base: "70px", md: "100px" }} pt={1}>{t('detail.fields.icon', { defaultValue: 'Icon' })}</Text>
        <EmojiPickerField
          value={editedCategory?.icon || ''}
          onChange={(emoji) => handleFieldChange('icon', emoji)}
          placeholder="🚗"
          variant="inline"
        />
      </Flex>

      <Flex py="8px" align="center">
        <Text fontSize={{ base: "sm", md: "md" }} color="redesign.text.muted" w={{ base: "70px", md: "100px" }}>{t('detail.fields.color', { defaultValue: 'Color' })}</Text>
        <ColorPicker
          value={editedCategory?.color || ''}
          onChange={(color) => handleFieldChange('color', color)}
        />
      </Flex>

      <Box py="8px">
        <Text fontSize={{ base: "sm", md: "md" }} color="redesign.text.muted" mb={1}>{t('detail.fields.description')}</Text>
        {isEditingDescription ? (
          <Textarea
            value={editedCategory?.description || ''}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            onBlur={() => setIsEditingDescription(false)}
            autoFocus
            placeholder={t('detail.fields.descriptionPlaceholder')}
            size="sm"
            rows={3}
            fontSize={{ base: "sm", md: "md" }}
          />
        ) : (
          <Text
            fontSize={{ base: "sm", md: "md" }}
            color="redesign.text"
            whiteSpace="pre-wrap"
            onClick={() => setIsEditingDescription(true)}
            cursor="pointer"
            _hover={{ color: "redesign.accent.blue" }}
            minH="32px"
          >
            {editedCategory?.description || t('detail.fields.addDescription')}
          </Text>
        )}
      </Box>

      <Flex mt={4} gap={2}>
        <Button variant="primary" flex={1} size="sm" onClick={handleSave} isLoading={updating} isDisabled={!editedCategory?.name?.trim()}>
          {isCreateMode ? t('detail.buttons.create') : t('detail.buttons.save')}
        </Button>
        {!isCreateMode && (
          <Button variant="outline" size="sm" onClick={() => setShowEditForm(false)}>
            {t('detail.buttons.cancel')}
          </Button>
        )}
      </Flex>
    </Box>
  );

  const renderViewMode = () => (
    <Box flex={1} overflowY="auto" overflowX="hidden">
      {/* Category identity */}
      <Box px={5} pt={5} pb={4}>
        <Flex align="flex-start" justify="space-between" mb={1}>
          <Box>
            <Flex align="center" gap={1.5} mb={1}>
              <Box w="6px" h="6px" borderRadius="full" bg={catColor} />
              <Text fontSize="lg">{catIcon}</Text>
            </Flex>
            <Text fontSize="xl" fontWeight="bold" color="redesign.heading">{catName}</Text>
          </Box>
          <Box textAlign="right">
            <Text textStyle="microLabel" mb={1}>
              {typeFilter === 'income'
                ? t('summary.earnedIn', { month: selectedMonthLabel })
                : t('summary.spentIn', { month: selectedMonthLabel })}
            </Text>
            {selectedMonthTotals.length === 0 ? (
              <Figure size="md" code={spentCurrency || '$'} amount="0" display="block" />
            ) : (
              selectedMonthTotals.map(({ currency, total }) => (
                <Figure
                  key={currency}
                  size={selectedMonthTotals.length > 1 ? 'sm' : 'md'}
                  code={currency}
                  amount={total.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  display="block"
                />
              ))
            )}
            {/* MI-298: approximate single-currency total for the selected month */}
            {displayCurrency && (
              <Box mt={1}>
                <ConvertedTotal
                  data={convertedTotal}
                  loading={convertedLoading}
                  magnitude
                  size="md"
                  align="flex-end"
                />
              </Box>
            )}
          </Box>
        </Flex>

        {/* Timeline */}
        <Box mt={4} mb={1}>
          <CategoryTimelineChart
            monthlyTimeline={monthlyTimeline}
            categoryId={categoryId}
            color={catColor}
            selectedIdx={selectedMonthIdx}
            onSelect={onSelectMonth}
            currentMonthIdx={currentMonthIdx}
          />
        </Box>
      </Box>

      {/* Key metrics */}
      <Box px={5} py={4} borderTop="1px solid" borderColor="redesign.border">
        <CategoryKeyMetrics yearlyMetrics={yearlyMetrics} categoryId={categoryId} />
      </Box>

      {/* Transactions for the selected month */}
      <Box px={5}>
        <MovementsList
          filters={movementsFilters}
          dateRange={selectedMonthRange}
          emptyLabel={selectedMonthLabel}
          categories={categories}
          allTags={allTags}
          showAccountName
          onMovementClick={setSelectedMovementId}
          refreshKey={transactionsRefreshKey}
          currency={effectiveCurrency}
          shouldKeepAfterCategoryChange={shouldKeepAfterCategoryChange}
          onCategoryChanged={onCategoryUpdated}
        />
      </Box>
    </Box>
  );

  return (
    <Box h="100%" bg="redesign.bg.surface" display="flex" flexDirection="column" position="relative">
      {selectedMovementId && (
        <Box position="absolute" inset={0} bg="redesign.bg.surface" zIndex={2} display="flex" flexDirection="column">
          <MovementDetailPanel
            movementId={selectedMovementId}
            onClose={handleCloseOverlay}
            onMovementUpdated={handleOverlayMovementUpdated}
            onMovementDeleted={handleOverlayMovementUpdated}
            onSelectMovement={setSelectedMovementId}
          />
        </Box>
      )}
      {/* Header */}
      <Flex
        justify="space-between"
        align="center"
        px={{ base: 3, md: 5 }}
        py={3}
        borderBottom="1px solid"
        borderColor="redesign.border"
        flexShrink={0}
      >
        <Text fontSize="sm" fontWeight="500" color="redesign.text">
          {isCreateMode ? t('detail.headerNew') : t('detail.header')}
        </Text>
        <Flex gap={2} align="center">
          {!isCreateMode && !showEditForm && onCategoryUpdated && (
            <Button
              size="xs"
              variant="outline"
              onClick={() => { setShowEditForm(true); setEditedCategory(category); }}
            >
              {t('detail.edit')}
            </Button>
          )}
          {!isCreateMode && onDelete && (
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<Text fontSize="md">⋯</Text>}
                variant="ghost"
                size="sm"
                aria-label={t('detail.options')}
              />
              <MenuList>
                <MenuItem icon={<DeleteIcon />} color="redesign.accent.red" onClick={handleDeleteClick}>
                  {t('detail.delete')}
                </MenuItem>
              </MenuList>
            </Menu>
          )}
          <IconButton icon={<CloseIcon boxSize={2.5} />} size="sm" variant="ghost" onClick={onClose} aria-label={t('detail.close')} />
        </Flex>
      </Flex>

      {/* Content */}
      {loading ? (
        <Flex justify="center" align="center" flex={1}>
          <Spinner size="xl" />
        </Flex>
      ) : error ? (
        <Text color="redesign.accent.red" textAlign="center" py={12}>{error}</Text>
      ) : showEditForm || isCreateMode ? (
        <Box flex={1} overflowY="auto" overflowX="hidden">
          {renderEditForm()}
        </Box>
      ) : (
        renderViewMode()
      )}
    </Box>
  );
}
